import { motion } from 'framer-motion';
import {
  Layers,
  Award,
  TrendingUp,
  Wrench,
  CheckCircle,
  ArrowRight,
  Zap,
  Target,
  Lightbulb,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../components/MarketingLayout';
import Breadcrumb from '../components/Breadcrumb';
import PageHero from '../components/PageHero';
import { getPageHeroImageUrl } from '../config/cloudflareAssets';

export default function InternalAssessmentPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);
  const assessmentAreas = [
    {
      icon: Award,
      title: 'SWOT Analysis',
      subtitle: 'Complete Internal Picture',
      description:
        'Analyze your organization from four critical perspectives to understand where you have power and where you need improvement.',
      link: '/internal-assessment/swot-analysis',
      components: [
        {
          name: 'Strengths',
          description:
            'What do you do better than anyone else? What unique resources or capabilities do you have?',
          examples: [
            'Strong brand recognition',
            'Proprietary technology',
            'Talented team',
            'Loyal customer base',
          ],
        },
        {
          name: 'Weaknesses',
          description:
            'Where are you vulnerable? What do competitors do better? What resources are you missing?',
          examples: ['Limited capital', 'Outdated systems', 'Skill gaps', 'Poor distribution'],
        },
        {
          name: 'Opportunities',
          description: 'What external trends can you capitalize on? Where are the market gaps?',
          examples: [
            'Growing market segments',
            'New technology adoption',
            'Competitor weaknesses',
            'Regulatory changes',
          ],
        },
        {
          name: 'Threats',
          description: 'What external factors could harm you? What keeps you up at night?',
          examples: [
            'New entrants',
            'Changing customer preferences',
            'Economic downturn',
            'Supply chain risks',
          ],
        },
      ],
    },
    {
      icon: Zap,
      title: 'Core Competencies',
      subtitle: 'Your Unique Advantages',
      description:
        'Identify the few things you do exceptionally well that create competitive advantage and are hard for others to copy.',
      link: '/internal-assessment/core-competencies',
      components: [
        {
          name: 'Technical Excellence',
          description: 'Superior skills, knowledge, or capabilities in your domain',
          examples: [
            'Engineering expertise',
            'Data analytics',
            'Product design',
            'R&D capabilities',
          ],
        },
        {
          name: 'Operational Efficiency',
          description: 'Ability to execute better, faster, or cheaper than competitors',
          examples: [
            'Supply chain optimization',
            'Manufacturing processes',
            'Customer service',
            'Quality control',
          ],
        },
        {
          name: 'Market Access',
          description: 'Unique ability to reach and serve customers',
          examples: [
            'Distribution networks',
            'Brand strength',
            'Customer relationships',
            'Market knowledge',
          ],
        },
      ],
    },
    {
      icon: Wrench,
      title: 'Resource Audit',
      subtitle: 'Know What You Have',
      description:
        'Catalog your key resources and capabilities to understand what you can leverage for strategic advantage.',
      link: '/internal-assessment/resource-audit',
      components: [
        {
          name: 'Human Resources',
          description: 'Talent, skills, leadership, and organizational culture',
          examples: [
            'Team size and skills',
            'Leadership strength',
            'Company culture',
            'Employee engagement',
          ],
        },
        {
          name: 'Financial Resources',
          description: 'Capital, cash flow, and financial flexibility',
          examples: [
            'Available capital',
            'Revenue streams',
            'Profit margins',
            'Investment capacity',
          ],
        },
        {
          name: 'Physical & Tech Assets',
          description: 'Infrastructure, equipment, systems, and intellectual property',
          examples: ['Technology stack', 'Facilities', 'IP and patents', 'Data assets'],
        },
        {
          name: 'Intangible Assets',
          description: 'Brand, reputation, relationships, and knowledge',
          examples: ['Brand value', 'Customer trust', 'Partner network', 'Institutional knowledge'],
        },
      ],
    },
  ];

  const benefits = [
    {
      icon: CheckCircle,
      title: 'Identify what to build on',
      description:
        'Name the strengths that matter to customers and the capabilities your strategy can rely on.',
    },
    {
      icon: Target,
      title: 'Be clear about the gaps',
      description:
        'Bring resource and capability gaps into the plan while you can still make choices.',
    },
    {
      icon: Lightbulb,
      title: 'Match ambition to capacity',
      description: 'Review your people, capital and assets before deciding where to focus them.',
    },
    {
      icon: TrendingUp,
      title: 'Choose what to develop',
      description:
        'Identify the capabilities worth strengthening to support your preferred strategic direction.',
    },
  ];

  return (
    <MarketingLayout>
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb
            items={[
              { label: 'Strategic Framework', path: '/strategic-framework' },
              { label: 'Internal Assessment' },
            ]}
          />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        badge={
          <span className="inline-flex items-center px-4 py-2 bg-white/90 text-blue-800 rounded-full text-sm font-semibold backdrop-blur-sm">
            <Layers className="w-4 h-4 mr-2" />
            Step 3 of 5
          </span>
        }
        title="Build your strategy on what you can do well."
        subtitle="Take an honest look at your strengths, constraints and resources. Decide what to build on, what to improve and what your next strategic move will require."
      >
        <Link
          to="/sign-up"
          className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:opacity-90 transition-opacity"
        >
          Assess my company strengths
        </Link>
      </PageHero>

      {/* Assessment Areas */}
      <section className="public-section py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6">See the capabilities behind your ambition.</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Evaluate your organization across multiple dimensions to understand true capabilities
            </p>
          </motion.div>

          <div className="space-y-16 max-w-6xl mx-auto">
            {assessmentAreas.map((area, index) => (
              <motion.div
                key={index}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="public-card bg-gradient-to-br from-purple-50 to-white rounded-2xl p-8 border border-purple-200"
              >
                <div className="flex flex-col md:flex-row gap-8 mb-8">
                  <div className="flex-shrink-0">
                    <div className="bg-gradient-to-br from-purple-600 to-blue-600 w-20 h-20 rounded-xl flex items-center justify-center shadow-sm">
                      <area.icon className="w-10 h-10 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold mb-2">{area.title}</h3>
                    <p className="text-purple-600 font-semibold mb-4 text-lg">{area.subtitle}</p>
                    <p className="text-gray-700 text-lg">{area.description}</p>
                  </div>
                </div>

                {/* Components Grid */}
                <div className="grid md:grid-cols-2 gap-6 mb-4">
                  {area.components.map((component, idx) => (
                    <div
                      key={idx}
                      className="public-card bg-white p-6 rounded-xl border border-purple-200"
                    >
                      <h4 className="font-bold text-gray-900 mb-2">{component.name}</h4>
                      <p className="text-gray-600 mb-3 text-sm">{component.description}</p>
                      <div className="space-y-1">
                        <p className="text-xs font-semibold text-gray-700">Examples:</p>
                        {component.examples.map((example, i) => (
                          <div key={i} className="flex items-start">
                            <div className="bg-purple-400 w-1 h-1 rounded-full mr-2 mt-1.5 flex-shrink-0" />
                            <span className="text-xs text-gray-600">{example}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Learn More Link */}
                <Link
                  to={area.link}
                  className="inline-flex items-center text-purple-600 hover:text-purple-700 font-semibold transition-colors"
                >
                  Learn More About {area.title} <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="public-section py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">
                Choose a direction your company can support.
              </h2>
              <p className="text-xl text-gray-600">
                You can't build an effective strategy without honest self-awareness.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="public-card text-center bg-white p-6 rounded-xl shadow-sm"
                >
                  <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="public-section public-cta py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-4">
              Turn your assessment into a strategic choice.
            </h2>
            <p className="text-gray-600 text-center mb-8">
              Connect your market context and internal capabilities to the priorities you will
              pursue next.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/external-assessment"
                className="inline-flex items-center justify-center bg-white text-gray-700 px-6 py-3 rounded-lg font-semibold border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                ← Previous: External Assessment
              </Link>
              <Link to="/strategy-formulation" className="public-button public-button--primary">
                Next: Strategy Formulation <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
